// Add this to your QuotationHistory.jsx or QuotationPDFExport.jsx

import { Document, Packer, Paragraph, Table, TableCell, TableRow, WidthType, BorderStyle, AlignmentType, HeadingLevel, TextRun } from 'docx';
import { saveAs } from 'file-saver';

// Function to generate Word document
export const generateWordDocument = async (quotationData) => {
  try {
    // Calculate totals
    let itemsTotal = 0;
    let cgstTotal = 0;
    let sgstTotal = 0;
    
    quotationData.items?.forEach(item => {
      const qty = parseFloat(item.qty || 1);
      const unitPrice = parseFloat(item.unit_price || 0);
      const cgstPercent = parseFloat(item.cgst || 0);
      const sgstPercent = parseFloat(item.sgst || 0);
      
      const itemSubtotal = qty * unitPrice;
      itemsTotal += itemSubtotal;
      
      const itemCGST = itemSubtotal * (cgstPercent / 100);
      const itemSGST = itemSubtotal * (sgstPercent / 100);
      cgstTotal += itemCGST;
      sgstTotal += itemSGST;
    });
    
    const additionalCosts = [
      'diagnostics_fee',
      'labour_cost',
      'parts_estimate',
      'testing_and_commissioning',
      'packaging_and_shipping',
    ].reduce((sum, field) => sum + (parseFloat(quotationData[field]) || 0), 0);
    
    const subtotal = itemsTotal + additionalCosts;
    const discountAmount = subtotal * (parseFloat(quotationData.discount_percent || 0) / 100);
    const afterDiscount = subtotal - discountAmount;
    const gstTotal = cgstTotal + sgstTotal;
    const grandTotal = afterDiscount + gstTotal;

    // Helper function to format currency
    const formatCurrency = (amount) => {
      if (!amount) amount = 0;
      const symbols = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };
      const symbol = symbols[quotationData.currency] || '';
      
      return `${symbol}${new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount)}`;
    };

    // Helper function to format date
    const formatDate = (dateStr) => {
      try {
        return new Date(dateStr).toLocaleDateString('en-IN');
      } catch {
        return dateStr || 'N/A';
      }
    };

    // Create document sections
    const sections = [];

    // Header section
    const headerParagraphs = [
      new Paragraph({
        text: quotationData.company_info?.name || 'Company Name',
        heading: HeadingLevel.HEADING_1,
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
      }),
      new Paragraph({
        text: 'QUOTATION',
        heading: HeadingLevel.HEADING_2,
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 },
      }),
    ];

    // Company and Client Info Table
    const infoTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({ text: 'Company Details', bold: true }),
                new Paragraph({ text: quotationData.company_info?.name || '' }),
                new Paragraph({ text: quotationData.company_info?.address || '' }),
                new Paragraph({ text: `Email: ${quotationData.company_info?.email || ''}` }),
                new Paragraph({ text: `Contact: ${quotationData.company_info?.contact || ''}` }),
                new Paragraph({ text: `GST/VAT: ${quotationData.company_info?.gst_vat || 'N/A'}` }),
              ],
              width: { size: 50, type: WidthType.PERCENTAGE },
            }),
            new TableCell({
              children: [
                new Paragraph({ text: 'Bill To', bold: true }),
                new Paragraph({ text: quotationData.client_info?.name || '' }),
                new Paragraph({ text: quotationData.client_info?.address || '' }),
                new Paragraph({ text: `Email: ${quotationData.client_info?.email || ''}` }),
                new Paragraph({ text: `Contact: ${quotationData.client_info?.contact || ''}` }),
                new Paragraph({ text: `GST/VAT: ${quotationData.client_info?.gst_vat || 'N/A'}` }),
              ],
              width: { size: 50, type: WidthType.PERCENTAGE },
            }),
          ],
        }),
      ],
      margins: { top: 100, bottom: 100, left: 100, right: 100 },
    });

    // Quotation Details Table
    const detailsTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ text: 'Quotation #:', bold: true })] }),
            new TableCell({ children: [new Paragraph({ text: quotationData.quotation_number || 'Auto-generate' })] }),
            new TableCell({ children: [new Paragraph({ text: 'Date:', bold: true })] }),
            new TableCell({ children: [new Paragraph({ text: formatDate(quotationData.date) })] }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ text: 'Valid Until:', bold: true })] }),
            new TableCell({ children: [new Paragraph({ text: formatDate(quotationData.valid_till) })] }),
            new TableCell({ children: [new Paragraph({ text: 'Currency:', bold: true })] }),
            new TableCell({ children: [new Paragraph({ text: quotationData.currency || 'INR' })] }),
          ],
        }),
      ],
      margins: { top: 100, bottom: 100, left: 100, right: 100 },
    });

    // Scope of Work
    const scopeParagraphs = quotationData.scope_of_work ? [
      new Paragraph({ text: 'Scope of Work', heading: HeadingLevel.HEADING_3, spacing: { before: 400, after: 200 } }),
      new Paragraph({ text: quotationData.scope_of_work, spacing: { after: 400 } }),
    ] : [];

    // Items Table Header
    const itemsTableRows = [
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ text: '#', bold: true, alignment: AlignmentType.CENTER })] }),
          new TableCell({ children: [new Paragraph({ text: 'Description', bold: true })] }),
          new TableCell({ children: [new Paragraph({ text: 'Unit', bold: true, alignment: AlignmentType.CENTER })] }),
          new TableCell({ children: [new Paragraph({ text: 'Unit Price', bold: true, alignment: AlignmentType.RIGHT })] }),
          new TableCell({ children: [new Paragraph({ text: 'Qty', bold: true, alignment: AlignmentType.CENTER })] }),
          new TableCell({ children: [new Paragraph({ text: 'Subtotal', bold: true, alignment: AlignmentType.RIGHT })] }),
          new TableCell({ children: [new Paragraph({ text: 'CGST%', bold: true, alignment: AlignmentType.CENTER })] }),
          new TableCell({ children: [new Paragraph({ text: 'SGST%', bold: true, alignment: AlignmentType.CENTER })] }),
          new TableCell({ children: [new Paragraph({ text: 'Amount (incl. GST)', bold: true, alignment: AlignmentType.RIGHT })] }),
        ],
      }),
    ];

    // Items Table Rows
    quotationData.items?.forEach((item, index) => {
      const itemSubtotal = (item.qty || 0) * (item.unit_price || 0);
      const cgstAmount = itemSubtotal * ((item.cgst || 0) / 100);
      const sgstAmount = itemSubtotal * ((item.sgst || 0) / 100);
      const amountWithGST = itemSubtotal + cgstAmount + sgstAmount;

      itemsTableRows.push(
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ text: String(item.item_no || index + 1), alignment: AlignmentType.CENTER })] }),
            new TableCell({ children: [new Paragraph({ text: item.description || '' })] }),
            new TableCell({ children: [new Paragraph({ text: item.unit || '', alignment: AlignmentType.CENTER })] }),
            new TableCell({ children: [new Paragraph({ text: formatCurrency(item.unit_price || 0), alignment: AlignmentType.RIGHT })] }),
            new TableCell({ children: [new Paragraph({ text: String(item.qty || 0), alignment: AlignmentType.CENTER })] }),
            new TableCell({ children: [new Paragraph({ text: formatCurrency(itemSubtotal), alignment: AlignmentType.RIGHT })] }),
            new TableCell({ children: [new Paragraph({ text: `${item.cgst || 0}%`, alignment: AlignmentType.CENTER })] }),
            new TableCell({ children: [new Paragraph({ text: `${item.sgst || 0}%`, alignment: AlignmentType.CENTER })] }),
            new TableCell({ children: [new Paragraph({ text: formatCurrency(amountWithGST), alignment: AlignmentType.RIGHT })] }),
          ],
        })
      );
    });

    const itemsTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: itemsTableRows,
      margins: { top: 100, bottom: 100, left: 100, right: 100 },
    });

    // Additional Costs Table
    const additionalCostsRows = [
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ text: 'Additional Costs', bold: true, heading: HeadingLevel.HEADING_3 })] }),
          new TableCell({ children: [new Paragraph({ text: '' })] }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ text: 'Diagnostics/Evaluation Fee:' })] }),
          new TableCell({ children: [new Paragraph({ text: formatCurrency(parseFloat(quotationData.diagnostics_fee || 0)), alignment: AlignmentType.RIGHT })] }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ text: 'Labour & Repair Cost:' })] }),
          new TableCell({ children: [new Paragraph({ text: formatCurrency(parseFloat(quotationData.labour_cost || 0)), alignment: AlignmentType.RIGHT })] }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ text: 'Parts Estimate:' })] }),
          new TableCell({ children: [new Paragraph({ text: formatCurrency(parseFloat(quotationData.parts_estimate || 0)), alignment: AlignmentType.RIGHT })] }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ text: 'Testing & Commissioning:' })] }),
          new TableCell({ children: [new Paragraph({ text: formatCurrency(parseFloat(quotationData.testing_and_commissioning || 0)), alignment: AlignmentType.RIGHT })] }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ text: 'Packaging & Shipping:' })] }),
          new TableCell({ children: [new Paragraph({ text: formatCurrency(parseFloat(quotationData.packaging_and_shipping || 0)), alignment: AlignmentType.RIGHT })] }),
        ],
      }),
    ];

    const additionalCostsTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: additionalCostsRows,
      margins: { top: 100, bottom: 100, left: 100, right: 100 },
    });

    // Summary Table
    const summaryRows = [
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ text: 'Items Total:', bold: true })] }),
          new TableCell({ children: [new Paragraph({ text: formatCurrency(itemsTotal), alignment: AlignmentType.RIGHT })] }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ text: 'Additional Costs:', bold: true })] }),
          new TableCell({ children: [new Paragraph({ text: formatCurrency(additionalCosts), alignment: AlignmentType.RIGHT })] }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ text: 'Subtotal:', bold: true })] }),
          new TableCell({ children: [new Paragraph({ text: formatCurrency(subtotal), alignment: AlignmentType.RIGHT })] }),
        ],
      }),
    ];

    if (quotationData.discount_percent > 0) {
      summaryRows.push(
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ text: `Discount (${quotationData.discount_percent}%):`, bold: true })] }),
            new TableCell({ children: [new Paragraph({ text: `-${formatCurrency(discountAmount)}`, alignment: AlignmentType.RIGHT })] }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ text: 'After Discount:', bold: true })] }),
            new TableCell({ children: [new Paragraph({ text: formatCurrency(afterDiscount), alignment: AlignmentType.RIGHT })] }),
          ],
        })
      );
    }

    summaryRows.push(
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ text: 'Total CGST:', bold: true })] }),
          new TableCell({ children: [new Paragraph({ text: formatCurrency(cgstTotal), alignment: AlignmentType.RIGHT })] }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ text: 'Total SGST:', bold: true })] }),
          new TableCell({ children: [new Paragraph({ text: formatCurrency(sgstTotal), alignment: AlignmentType.RIGHT })] }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ text: 'Total GST:', bold: true })] }),
          new TableCell({ children: [new Paragraph({ text: formatCurrency(gstTotal), alignment: AlignmentType.RIGHT })] }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ text: 'GRAND TOTAL:', bold: true, heading: HeadingLevel.HEADING_3 })] }),
          new TableCell({ children: [new Paragraph({ text: formatCurrency(grandTotal), bold: true, heading: HeadingLevel.HEADING_3, alignment: AlignmentType.RIGHT })] }),
        ],
      })
    );

    const summaryTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: summaryRows,
      margins: { top: 100, bottom: 100, left: 100, right: 100 },
    });

    // Terms & Conditions
    const termsTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ text: 'Terms & Conditions', bold: true, heading: HeadingLevel.HEADING_3 })] }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ text: 'Warranty:' })] }),
            new TableCell({ children: [new Paragraph({ text: `${quotationData.warranty?.months || 0} months` })] }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ text: 'Lead Time:' })] }),
            new TableCell({ children: [new Paragraph({ text: `${quotationData.lead_time_days || 0} days` })] }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ text: 'Payment Terms:' })] }),
            new TableCell({ children: [new Paragraph({ text: quotationData.payment_terms || '' })] }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ text: 'Delivery Terms:' })] }),
            new TableCell({ children: [new Paragraph({ text: quotationData.delivery_terms || '' })] }),
          ],
        }),
      ],
      margins: { top: 100, bottom: 100, left: 100, right: 100 },
    });

    // Notes
    const notesParagraphs = quotationData.notes ? [
      new Paragraph({ text: 'Notes', heading: HeadingLevel.HEADING_3, spacing: { before: 400, after: 200 } }),
      new Paragraph({ text: quotationData.notes, spacing: { after: 400 } }),
    ] : [];

    // Create the document
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: [
            ...headerParagraphs,
            infoTable,
            new Paragraph({ text: '', spacing: { after: 400 } }),
            detailsTable,
            ...scopeParagraphs,
            new Paragraph({ text: 'Items & Services', heading: HeadingLevel.HEADING_3, spacing: { before: 400, after: 200 } }),
            itemsTable,
            new Paragraph({ text: '', spacing: { after: 400 } }),
            additionalCostsTable,
            new Paragraph({ text: '', spacing: { after: 400 } }),
            new Paragraph({ text: 'Summary', heading: HeadingLevel.HEADING_3, spacing: { before: 400, after: 200 } }),
            summaryTable,
            new Paragraph({ text: '', spacing: { after: 400 } }),
            termsTable,
            ...notesParagraphs,
          ],
        },
      ],
    });

    // Generate and save the document
    const blob = await Packer.toBlob(doc);
    
    // Get today's date for filename
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const todayFormatted = `${day}_${month}_${year}`;
    
    const fileName = `Quotation_${quotationData.quotation_number || 'Draft'}_${todayFormatted}.docx`;
    saveAs(blob, fileName);

    return { success: true };
  } catch (error) {
    //console.error('Word document generation error:', error);
    throw error;
  }
};